<template>
    <div class="space-y-10">
        <div v-if="collection">
            <div
                v-if="collection.name || collection.description"
                class="flex items-end justify-between space-x-10 space-y-5 md:space-y-0"
            >
                <div class="w-full">
                    <CHeading v-if="collection.name" :title="collection.name">
                        {{ collection.name }}
                    </CHeading>

                    <div
                        v-if="collection.description"
                        class="mb-0 block max-w-4xl"
                        v-html="collection.description"
                    ></div>
                </div>
            </div>

            <div v-if="collection.newsletter?.hasNewsletter" class="pb-4 sm:pb-6">
                <EmailNewsletterBlock class="rounded-xl" :custom-texts="collection.newsletter" />
            </div>
            <!-- padding-top 73 pixels so navbar won't overlap header after anchoring-->
            <div
                v-for="(section, sectionIndex) in visibleSections"
                :id="section.anchor"
                :key="sectionIndex"
                class="pt-[73px]"
            >
                <div
                    v-if="section.name || section.description"
                    class="mb-8 flex items-end justify-between space-x-10 space-y-5 md:space-y-0"
                >
                    <div class="w-full">
                        <CHeading v-if="section.name" tag="h2" :title="section.name" :variant="1">
                            {{ section.name }}
                        </CHeading>

                        <div v-if="section.description" class="mb-0 block max-w-4xl" v-html="section.description" />
                    </div>
                </div>

                <div class="space-y-4">
                    <MoreItemsCardGrid
                        :items="section.selections.flatMap((selection, idx, sel) => mapSelections(selection, idx, sel)).filter(Boolean)"
                        :visible-items="section.selections.reduce((acc, curr) => countVisibleItems(acc, curr), 0) - 1"
                    />
                </div>
            </div>

            <div v-if="blogPage?.posts.length" class="pt-5">
                <CHeading tag="h2" title="BlogPosts" :variant="1"> Blog </CHeading>
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <BlogPostCard v-for="post in blogPage.posts" :key="post.id" :post="post" />
                </div>
                <div v-if="blogPage.total > blogPage.posts.length" class="mt-12 text-center">
                    <CButton :loading="blogPostsStatus === 'pending'" @click="blogPostsLimit += BLOG_POSTS_PAGE_SIZE">
                        Zobraziť ďalšie
                    </CButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import type { BlogPost } from '#shared/types/Blog'
import type { Collection } from '#shared/types/collection'
import { useCollections } from '@/composables/collections/useCollections'
import { useHeadMeta } from '@/composables/useHeadMeta'

const route = useRoute()
const router = useRouter()
const { getCollection } = useCollections()
const { getHeadMetaOpenGraphData, headMetaTrackGTM } = useHeadMeta()


const { data: collection, error } = await useAsyncData('collection', async () => {
    // Check if slug parameter exists and is not empty
    const slug = route.params.slug as string
    if (!slug || slug === '' || slug === 'index') {
        throw createError({ statusCode: 404, message: 'Collection slug is required' })
    }

    try {
        const result = await getCollection(slug)
        return result
    } catch (e) {
        throw createError({ statusCode: 404, message: 'Collection not found' })
    }
})

// useAsyncData captures a thrown error into `error` instead of propagating it,
// so the 404s above never reached the response — this catch-all route answered
// HTTP 200 for every unknown URL. That is an unbounded space of near-duplicate
// crawlable pages; Google's guidance is explicit about not wasting crawl budget
// on duplicates. `fatal` renders the error page with the real status.
if (error.value) {
    throw createError({ statusCode: 404, statusMessage: 'Stránka sa nenašla', fatal: true })
}

// A section whose picks have all been closed or unpublished would render as a bare heading
// ("Aktuálne hľadáme:" with nothing under it), so it is dropped. Text-only sections stay.
const visibleSections = computed(() =>
    (collection.value?.sections ?? []).filter((section: any) => {
        const selections = section.selections ?? []
        const hasPicks = selections.some((s: any) => s.items?.length || s.tags?.length || s.who?.length)
        return !hasPicks || selections.some((s: any) => s.opportunities?.length)
    })
)

// Articles from the GROWNi blog carrying any of the collection's tags.
const BLOG_POSTS_PAGE_SIZE = 4
const blogPostsLimit = ref(BLOG_POSTS_PAGE_SIZE)
const blogTags = computed(() => collection.value?.blogTags?.join(',') || '')
const { data: blogPage, status: blogPostsStatus } = await useFetch<{ posts: BlogPost[]; total: number }>('/api/blogs', {
    key: `collection-blog-${route.params.slug}`,
    query: computed(() => ({ tags: blogTags.value, paginated: 'true', summary: 'true', limit: blogPostsLimit.value })),
    immediate: Boolean(blogTags.value)
})

const countVisibleItems = (total: number, curr: any) => {
    // Max 4 items in row, sum max visible items in rows
    const maxVisibleItems = Number(curr.rows) * 4
    // If opportunities count less than max within rows, return count
    return total + (maxVisibleItems < curr.opportunities.length ? maxVisibleItems : curr.opportunities.length)
}

// map selections opportunities according to defined row count, remaining opportunities placing at the end
const mapSelections = (selection: any, index: number, selections: any[]) => {
    if (index + 1 === selections.length) {
        return selection.rows
            ? [
                  ...selection.opportunities.slice(0, selection.rows * 4),
                  ...selections.flatMap((s) => s.opportunities.slice(s.rows! * 4, s.opportunities.length))
              ]
            : selection.opportunities
    }
    return selection.rows ? selection.opportunities.slice(0, selection.rows * 4) : selection.opportunities
}

// Meta tags
const seo = computed(() => collection.value?.seo || ({} as Collection['seo']))

useHead({
    title: seo.value.title,
    meta: [
        {
            key: 'og:url',
            property: 'og:url',
            content: 'https://growni.sk' + route.fullPath
        },
        ...getHeadMetaOpenGraphData('title', seo.value.title, true),
        ...getHeadMetaOpenGraphData('image', seo.value.image, true),
        ...getHeadMetaOpenGraphData('image:alt', seo.value.title, true),
        ...getHeadMetaOpenGraphData('description', seo.value.description)
    ],
    ...headMetaTrackGTM(
        `event: "virtual_page_view",
        page_type: "landing_page",
        modul_type: "collections",
        has_cta: "false",
        unique_id: "${route.path.slice(1, route.path.length)}"`
    )
})

// Add page meta to help with routing priority
definePageMeta({
    validate: async (route) => {
        // Ensure we have a slug parameter and it's not empty or 'index'
        const slug = route.params.slug as string
        return typeof slug === 'string' && slug.length > 0 && slug !== 'index'
    }
})
</script>
